import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RobotPartDto } from '@bhops/shared';
import { partsApi } from '@/api/parts';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';

// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.5 — 부품 제거 모달.
//
// 활성 RobotPart 를 명시적 사유로 제거. 자동 UPGRADE swap 과 달리 사유가
// audit log 에 기록되고 LOT 결함 통계에 영향.

const REASON_LABEL: Record<string, string> = {
  WEAR: '마모: 정상 수명 종료',
  DEFECT: '결함: 비정상 고장',
  UPGRADE: '업그레이드: 신형 부품이나 펌웨어',
  RECALL: '리콜: LOT 결함 대응',
  OTHER: '기타',
};
const REASON_KEYS = Object.keys(REASON_LABEL);

export interface PartRemoveModalProps {
  open: boolean;
  onClose: () => void;
  part: RobotPartDto | null;
  robotId: string;
}

export function PartRemoveModal({ open, onClose, part, robotId }: PartRemoveModalProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const [removalReason, setRemovalReason] = useState<string>('WEAR');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // reset on open
  useEffect(() => {
    if (open) {
      setRemovalReason('WEAR');
      setNotes('');
      setError(null);
    }
  }, [open]);

  const removeMx = useMutation({
    mutationFn: () => {
      if (!part) throw new Error('No part selected');
      return partsApi.removePart(part.id, {
        removalReason,
        notes: notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot-parts', robotId] });
      qc.invalidateQueries({ queryKey: ['part-lots'] });
      toast.success('부품을 제거했습니다.');
      handleClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleClose(): void {
    if (removeMx.isPending) return;
    onClose();
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!part) return;
    removeMx.mutate();
  }

  if (!part) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="부품 제거"
      width={480}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={removeMx.isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleSubmit}
            disabled={removeMx.isPending}
            data-testid="part-remove-submit"
          >
            {removeMx.isPending ? '제거 중…' : '제거'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {error && <FormError>{error}</FormError>}

        <div
          style={{
            padding: 10,
            background: 'var(--bg-sunken)',
            borderRadius: 'var(--r-card)',
            marginBottom: 4,
            fontSize: 13,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>대상</div>
          <div style={{ fontWeight: 600, marginTop: 2 }}>
            [{part.slot}] {part.partCatalog.displayName}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-mono)',
              marginTop: 2,
            }}
          >
            SKU {part.partCatalog.sku}
            {part.partLot && ` · LOT ${part.partLot.lotCode}`}
            {part.serialNo && ` · SN ${part.serialNo}`}
          </div>
        </div>

        <Field label="제거 사유" htmlFor="reason" required>
          <Select
            id="reason"
            value={removalReason}
            onChange={(e) => setRemovalReason(e.target.value)}
            data-testid="part-removal-reason"
          >
            {REASON_KEYS.map((k) => (
              <option key={k} value={k}>
                {REASON_LABEL[k]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="비고 (선택)" htmlFor="notes" hint="자세한 사유나 점검 결과를 적어 주세요">
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="예: 그릴 표면에 균열이 있어 안전을 위해 바로 교체"
            rows={3}
          />
        </Field>

        {removalReason === 'DEFECT' && (
          <div
            style={{
              padding: 10,
              background: 'var(--bg-warning-subtle)',
              border: '1px solid var(--severity-warning)',
              borderRadius: 'var(--r-card)',
              fontSize: 12,
              marginTop: 4,
            }}
          >
            {/* 종전 문구는 '추적 · 분석 페이지 또는 부품 LOT 목록에서 확인 가능' 이었다.
                '부품 LOT 목록' 은 사이드바 '부품 입고' 인데, 오너 결정 2026-08-28 로
                그 메뉴가 숨겨졌다(nav-hidden.ts). 이 창은 숨긴 화면이 아니라 로봇 상세
                '부품' 탭에서 뜨므로, 사용자는 **매일 보면서 갈 수 없는 곳**을 안내받게
                된다. 그래서 지금 실제로 열려 있는 두 길만 남긴다: 추적 · 분석 화면과
                이 탭의 LOT 링크(LOT 영향권 화면 — 라우트는 그대로 살아 있다). */}
            결함 부품으로 표시하면 같은 LOT이 결함 자동 감지 대상에 들어갑니다. LOT 결함이 의심되면
            '추적 · 분석' 화면이나 이 '부품' 탭의 LOT을 눌러 확인할 수 있습니다.
          </div>
        )}
      </form>
    </Modal>
  );
}
